import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { UploadRequest, UploadResult, ProcessingStatus } from '@nvgc/shared';

interface FileUploadItem {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  result?: UploadResult;
  error?: string;
}

interface DocumentUploadProps {
  onUploadComplete?: (result: UploadResult) => void;
  onUploadError?: (error: string) => void;
  maxFileSize?: number; // in bytes
  acceptedFormats?: string[];
}

const DEFAULT_ACCEPTED_FORMATS = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg'
];

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onUploadComplete,
  onUploadError,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  acceptedFormats = DEFAULT_ACCEPTED_FORMATS
}) => {
  const [uploadQueue, setUploadQueue] = useState<FileUploadItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    if (!acceptedFormats.includes(file.type)) {
      return `Unsupported file type: ${file.type}. Allowed formats: PDF, DOCX, PNG, JPG`;
    }
    
    if (file.size > maxFileSize) {
      const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
      return `File size exceeds ${maxSizeMB}MB limit`;
    }
    
    return null;
  }, [acceptedFormats, maxFileSize]);

  const addFilesToQueue = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: FileUploadItem[] = [];
    const errors: string[] = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push({
          id: crypto.randomUUID(),
          file,
          status: 'pending',
          progress: 0
        });
      }
    });

    if (errors.length > 0) {
      setErrorMessage(errors.join('\n'));
      setShowErrorDialog(true);
    }

    if (validFiles.length > 0) {
      setUploadQueue(prev => [...prev, ...validFiles]);
      // Start uploading immediately
      validFiles.forEach(item => uploadFile(item));
    }
  }, [validateFile]);

  const uploadFile = async (item: FileUploadItem) => {
    try {
      // Update status to uploading
      setUploadQueue(prev => 
        prev.map(q => q.id === item.id ? { ...q, status: 'uploading', progress: 0 } : q)
      );

      const formData = new FormData();
      formData.append('file', item.file);
      
      const uploadRequest: UploadRequest = {
        fileName: item.file.name,
        fileSize: item.file.size,
        mimeType: item.file.type
      };
      
      formData.append('metadata', JSON.stringify(uploadRequest.metadata || {}));

      const response = await fetch('/api/documents/upload', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(errorData.message || 'Upload failed');
      }

      const result: UploadResult = await response.json();

      // Update status to processing
      setUploadQueue(prev => 
        prev.map(q => q.id === item.id ? { 
          ...q, 
          status: 'processing', 
          progress: 50,
          result 
        } : q)
      );

      // Start polling for processing status
      pollProcessingStatus(item.id, result.documentId);

      onUploadComplete?.(result);

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Upload failed';
      
      setUploadQueue(prev => 
        prev.map(q => q.id === item.id ? { 
          ...q, 
          status: 'failed', 
          error: errorMsg 
        } : q)
      );

      onUploadError?.(errorMsg);
    }
  };

  const pollProcessingStatus = async (queueItemId: string, documentId: string) => {
    const maxAttempts = 60; // 5 minutes with 5-second intervals
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(`/api/documents/${documentId}/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch processing status');
        }

        const status: ProcessingStatus = await response.json();

        setUploadQueue(prev => 
          prev.map(q => q.id === queueItemId ? { 
            ...q, 
            progress: status.progress,
            status: status.status === 'completed' ? 'completed' : 
                   status.status === 'failed' ? 'failed' : 'processing',
            error: status.error
          } : q)
        );

        if (status.status === 'completed' || status.status === 'failed') {
          return; // Stop polling
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000); // Poll every 5 seconds
        } else {
          // Timeout - mark as completed with warning
          setUploadQueue(prev => 
            prev.map(q => q.id === queueItemId ? { 
              ...q, 
              status: 'completed',
              progress: 100,
              error: 'Processing status unknown (timeout)'
            } : q)
          );
        }

      } catch (error) {
        console.error('Error polling processing status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        }
      }
    };

    poll();
  };

  const removeFromQueue = useCallback((id: string) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      addFilesToQueue(files);
    }
  }, [addFilesToQueue]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      addFilesToQueue(files);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [addFilesToQueue]);

  const getStatusIcon = (status: FileUploadItem['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'uploading':
      case 'processing':
        return <PauseIcon color="primary" />;
      default:
        return <PlayArrowIcon color="action" />;
    }
  };

  const getStatusColor = (status: FileUploadItem['status']) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'uploading':
      case 'processing':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* Upload Drop Zone */}
      <Paper
        elevation={isDragOver ? 4 : 1}
        sx={{
          p: 4,
          textAlign: 'center',
          border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
          backgroundColor: isDragOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: '#1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.02)'
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Drop files here or click to browse
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Supported formats: PDF, DOCX, PNG, JPG
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Maximum file size: {Math.round(maxFileSize / (1024 * 1024))}MB
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          sx={{ mt: 2 }}
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
        >
          Select Files
        </Button>
      </Paper>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFormats.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Upload Queue ({uploadQueue.length})
          </Typography>
          <List>
            {uploadQueue.map((item) => (
              <ListItem key={item.id} divider>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  {getStatusIcon(item.status)}
                </Box>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">{item.file.name}</Typography>
                      <Chip 
                        label={item.status} 
                        size="small" 
                        color={getStatusColor(item.status) as any}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(item.file.size)} • {item.file.type}
                      </Typography>
                      {(item.status === 'uploading' || item.status === 'processing') && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={item.progress} 
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {item.progress}% • {item.status === 'uploading' ? 'Uploading...' : 'Processing...'}
                          </Typography>
                        </Box>
                      )}
                      {item.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {item.error}
                        </Alert>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    onClick={() => removeFromQueue(item.id)}
                    disabled={item.status === 'uploading'}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Error Dialog */}
      <Dialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)}>
        <DialogTitle>Upload Errors</DialogTitle>
        <DialogContent>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};